import {
  Box,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Button,
} from "@mui/material";
import { IAddress } from "../../types/seed";
import {
  fetchAddressFromCEP,
  formatCEP,
  isValidCEP,
  BRAZILIAN_STATES,
} from "../../utils/addressUtils";
import { useState } from "react";

interface AddressFieldsetProps {
  address: IAddress | null;
  onChange: (address: IAddress | null) => void;
  title?: string;
}

const createEmptyAddress = (
  addressType: IAddress["address_type"] = "full",
): IAddress => ({
  address_type: addressType,
  location_reference: "",
  street: "",
  number: "",
  complement: "",
  neighborhood: "",
  city: "",
  state: "",
  postal_code: "",
  country_code: "BR",
  latitude: undefined,
  longitude: undefined,
});

const parseCoordinate = (value: string) => {
  if (value === "") {
    return undefined;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const AddressFieldset = ({
  address,
  onChange,
  title = "Endereço Detalhado",
}: AddressFieldsetProps) => {
  const [isLoadingCEP, setIsLoadingCEP] = useState(false);
  const [cepError, setCepError] = useState<string>("");
  const addressType = address?.address_type || "full";

  const handleFieldChange = (
    field: keyof IAddress,
    value: string | number | undefined,
  ) => {
    if (address) {
      onChange({
        ...address,
        [field]: value,
      });
    } else {
      onChange({
        ...createEmptyAddress(),
        [field]: value,
      });
    }
  };

  const handleAddressTypeChange = (nextType: IAddress["address_type"]) => {
    const baseAddress = address || createEmptyAddress(nextType);
    onChange({
      ...baseAddress,
      address_type: nextType,
    });
    setCepError("");
  };

  const handleCEPChange = async (cep: string) => {
    const formattedCep = formatCEP(cep);
    handleFieldChange("postal_code", formattedCep);
    setCepError("");

    // Only fetch if CEP is complete and valid
    if (isValidCEP(cep)) {
      setIsLoadingCEP(true);
      try {
        const addressData = await fetchAddressFromCEP(cep);
        if (addressData) {
          // Update address with fetched data
          const updatedAddress: IAddress = {
            ...address!,
            address_type: address?.address_type || "full",
            postal_code: formattedCep,
            street: addressData.street,
            neighborhood: addressData.neighborhood,
            city: addressData.city,
            state: addressData.state,
            country_code: "BR",
          };
          onChange(updatedAddress);
        } else {
          setCepError("CEP não encontrado");
        }
      } catch (error) {
        setCepError("Erro ao buscar CEP");
      } finally {
        setIsLoadingCEP(false);
      }
    }
  };

  const handleRemove = () => {
    onChange(null);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
        border: "1px solid #e0e0e0",
        borderRadius: "8px",
        padding: "1.5rem",
        backgroundColor: "#fafafa",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6" component="h3">
          {title}
        </Typography>
        {address && (
          <Typography
            variant="body2"
            color="error"
            sx={{ cursor: "pointer", textDecoration: "underline" }}
            onClick={handleRemove}
          >
            Remover endereço
          </Typography>
        )}
      </Box>

      {address ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
          }}
        >
          <FormControl sx={{ maxWidth: "320px" }}>
            <InputLabel>Tipo de localização</InputLabel>
            <Select
              value={addressType}
              onChange={(e) =>
                handleAddressTypeChange(
                  e.target.value as IAddress["address_type"],
                )
              }
              label="Tipo de localização"
            >
              <MenuItem value="full">Endereço completo</MenuItem>
              <MenuItem value="coordinates">Localização rural</MenuItem>
            </Select>
          </FormControl>

          {addressType === "coordinates" && (
            <TextField
              label="Referência do local"
              value={address.location_reference || ""}
              onChange={(e) =>
                handleFieldChange("location_reference", e.target.value)
              }
              placeholder="Sítio Boa Vista, Linha São Pedro, km 8"
              required
              fullWidth
            />
          )}

          {/* CEP field first for prefilling */}
          {addressType === "full" && (
            <Box sx={{ display: "flex", gap: "1rem" }}>
              <TextField
                label="CEP"
                value={address.postal_code}
                onChange={(e) => handleCEPChange(e.target.value)}
                sx={{ minWidth: "150px" }}
                required
                placeholder="00000-000"
                error={!!cepError}
                helperText={cepError}
                InputProps={{
                  endAdornment: isLoadingCEP ? (
                    <CircularProgress size={20} />
                  ) : null,
                }}
              />
            </Box>
          )}

          {addressType === "full" && cepError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {cepError}
            </Alert>
          )}

          {/* Address fields */}
          {addressType === "full" && (
            <Box sx={{ display: "flex", gap: "1rem" }}>
              <TextField
                label="Logradouro"
                value={address.street}
                onChange={(e) => handleFieldChange("street", e.target.value)}
                fullWidth
                required
              />
              <TextField
                label="Número"
                value={address.number}
                onChange={(e) => handleFieldChange("number", e.target.value)}
                sx={{ minWidth: "120px" }}
                required
              />
            </Box>
          )}

          {addressType === "full" && (
            <TextField
              label="Complemento"
              value={address.complement || ""}
              onChange={(e) => handleFieldChange("complement", e.target.value)}
              placeholder="Apartamento, casa, etc."
            />
          )}

          {/* Location fields - rearranged for better state field width */}
          <Box sx={{ display: "flex", gap: "1rem" }}>
            {addressType === "full" && (
              <TextField
                label="Bairro"
                value={address.neighborhood}
                onChange={(e) =>
                  handleFieldChange("neighborhood", e.target.value)
                }
                sx={{ flex: 1 }}
                required
              />
            )}
            <TextField
              label="Cidade"
              value={address.city}
              onChange={(e) => handleFieldChange("city", e.target.value)}
              sx={{ flex: 1 }}
              required
            />
            <FormControl sx={{ minWidth: "180px" }}>
              <InputLabel>Estado</InputLabel>
              <Select
                value={address.state}
                onChange={(e) => handleFieldChange("state", e.target.value)}
                label="Estado"
                required
              >
                {BRAZILIAN_STATES.map((state) => (
                  <MenuItem key={state.code} value={state.code}>
                    {state.code}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ display: "flex", gap: "1rem" }}>
            <TextField
              label="Latitude"
              type="number"
              value={address.latitude ?? ""}
              onChange={(e) =>
                handleFieldChange("latitude", parseCoordinate(e.target.value))
              }
              sx={{ minWidth: "150px" }}
              inputProps={{ step: "any" }}
              required={addressType === "coordinates"}
            />
            <TextField
              label="Longitude"
              type="number"
              value={address.longitude ?? ""}
              onChange={(e) =>
                handleFieldChange("longitude", parseCoordinate(e.target.value))
              }
              sx={{ minWidth: "150px" }}
              inputProps={{ step: "any" }}
              required={addressType === "coordinates"}
            />
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "2rem",
            border: "2px dashed #ccc",
            borderRadius: "8px",
            backgroundColor: "white",
          }}
        >
          <Box sx={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              onClick={() => {
                onChange(createEmptyAddress("full"));
                setCepError("");
              }}
            >
              Endereço completo
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                onChange(createEmptyAddress("coordinates"));
                setCepError("");
              }}
            >
              Localização rural
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default AddressFieldset;
